import { Button, Grid } from '@material-ui/core';
import {
	EntityApiDefinitionCard,
	EntityConsumedApisCard,
	EntityConsumingComponentsCard,
	EntityHasApisCard,
	EntityProvidedApisCard,
	EntityProvidingComponentsCard,
} from '@backstage/plugin-api-docs';
import {
	EntityAboutCard,
	EntityDependsOnComponentsCard,
	EntityDependsOnResourcesCard,
	EntityHasComponentsCard,
	EntityHasResourcesCard,
	EntityHasSubcomponentsCard,
	EntityHasSystemsCard,
	EntityLayout,
	EntityLinksCard,
	EntitySwitch,
	EntityOrphanWarning,
	EntityProcessingErrorsPanel,
	isComponentType,
	isKind,
	hasCatalogProcessingErrors,
	isOrphan,
	hasRelationWarnings,
	EntityRelationWarning,
} from '@backstage/plugin-catalog';
import {
	isGithubActionsAvailable,
	EntityGithubActionsContent,
} from '@backstage-community/plugin-github-actions';
import {
	EntityUserProfileCard,
	EntityGroupProfileCard,
	EntityMembersListCard,
	EntityOwnershipCard,
} from '@backstage/plugin-org';
import { EntityTechdocsContent } from '@backstage/plugin-techdocs';
import { EmptyState } from '@backstage/core-components';
import {
	Direction,
	EntityCatalogGraphCard,
} from '@backstage/plugin-catalog-graph';
import {
	RELATION_API_CONSUMED_BY,
	RELATION_API_PROVIDED_BY,
	RELATION_CONSUMES_API,
	RELATION_DEPENDENCY_OF,
	RELATION_DEPENDS_ON,
	RELATION_HAS_PART,
	RELATION_PART_OF,
	RELATION_PROVIDES_API,
} from '@backstage/catalog-model';

import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';

import { EntityGithubInsightsContent } from '@roadiehq/backstage-plugin-github-insights';
import {
	EntityGithubGroupPullRequestsCard,
	EntityGithubPullRequestsContent,
	isGithubPullRequestsAvailable,
	isGithubTeamPullRequestsAvailable,
} from '@roadiehq/backstage-plugin-github-pull-requests';

import { ComponentEntityWorkflowsCard } from '@internal/plugin-github-actions-extensions';

import { GroupEntityWorkflowsCard } from '@internal/plugin-github-actions-extensions';

import { SkillExchangeUserEntitySkillsCard } from '@spotify/backstage-plugin-skill-exchange';
import { EntityTeamMetadataCard } from '@internal/plugin-team-metadata';

// import { EntitySoundcheckContent, EntitySoundcheckCard, GroupSoundcheckContent } from '@spotify/backstage-plugin-soundcheck';

const techdocsContent = (
	<EntityTechdocsContent>
		<TechDocsAddons>
			<ReportIssue />
		</TechDocsAddons>
	</EntityTechdocsContent>
);

const cicdContent = (
	// This is an example of how you can implement your company's logic in entity page.
	// You can for example enforce that all components of type 'service' should use GitHubActions
	<EntitySwitch>
		<EntitySwitch.Case if={isGithubActionsAvailable}>
			<EntityGithubActionsContent />
		</EntitySwitch.Case>

		<EntitySwitch.Case>
			<EmptyState
				title="No CI/CD available for this entity"
				missing="info"
				description="You need to add an annotation to your component if you want to enable CI/CD for it. You can read more about annotations in Backstage by clicking the button below."
				action={
					<Button
						variant="contained"
						color="primary"
						href="https://backstage.io/docs/features/software-catalog/well-known-annotations"
					>
						Read more
					</Button>
				}
			/>
		</EntitySwitch.Case>
	</EntitySwitch>
);

export const githubActionsHealthRoute = (
	<EntityLayout.Route
		path="/github-actions-health"
		title="GitHub Actions Health"
	>
		<Grid container spacing={3}>
			<Grid item xs={12} md={12}>
				<EntitySwitch>
					<EntitySwitch.Case if={isKind('component')}>
						<ComponentEntityWorkflowsCard />
					</EntitySwitch.Case>
					<EntitySwitch.Case if={isKind('group')}>
						<GroupEntityWorkflowsCard />
					</EntitySwitch.Case>
				</EntitySwitch>
			</Grid>
		</Grid>
	</EntityLayout.Route>
);

const entityWarningContent = (
	<>
		<EntitySwitch>
			<EntitySwitch.Case if={isOrphan}>
				<Grid item xs={12}>
					<EntityOrphanWarning />
				</Grid>
			</EntitySwitch.Case>
		</EntitySwitch>

		<EntitySwitch>
			<EntitySwitch.Case if={hasRelationWarnings}>
				<Grid item xs={12}>
					<EntityRelationWarning />
				</Grid>
			</EntitySwitch.Case>
		</EntitySwitch>

		<EntitySwitch>
			<EntitySwitch.Case if={hasCatalogProcessingErrors}>
				<Grid item xs={12}>
					<EntityProcessingErrorsPanel />
				</Grid>
			</EntitySwitch.Case>
		</EntitySwitch>
	</>
);

const overviewContent = (
	<Grid container spacing={3} alignItems="stretch">
		{entityWarningContent}
		<Grid item md={6}>
			<EntityAboutCard />
		</Grid>
		<Grid item md={6} xs={12}>
			<EntityCatalogGraphCard height={400} />
		</Grid>
		{/* <Grid item md={12} xs={12}>
      <EntitySoundcheckCard />
    </Grid> */}
		<Grid item md={4} xs={12}>
			<EntityLinksCard />
		</Grid>
		<Grid item md={8} xs={12}>
			<EntityHasSubcomponentsCard />
		</Grid>
	</Grid>
);

const serviceEntityPage = (
	<EntityLayout>
		<EntityLayout.Route path="/" title="Overview">
			{overviewContent}
		</EntityLayout.Route>

		<EntityLayout.Route path="/code-insights" title="Code Insights">
			<EntityGithubInsightsContent />
		</EntityLayout.Route>

		<EntityLayout.Route path="/ci-cd" title="CI/CD">
			{cicdContent}
		</EntityLayout.Route>

		<EntityLayout.Route
			path="/pull-requests"
			title="Pull Requests"
			if={isGithubPullRequestsAvailable}
		>
			<EntityGithubPullRequestsContent />
		</EntityLayout.Route>

		<EntityLayout.Route path="/api" title="API">
			<Grid container spacing={3} alignItems="stretch">
				<Grid item md={6}>
					<EntityProvidedApisCard />
				</Grid>
				<Grid item md={6}>
					<EntityConsumedApisCard />
				</Grid>
			</Grid>
		</EntityLayout.Route>

		<EntityLayout.Route path="/dependencies" title="Dependencies">
			<Grid container spacing={3} alignItems="stretch">
				<Grid item md={6}>
					<EntityDependsOnComponentsCard />
				</Grid>
				<Grid item md={6}>
					<EntityDependsOnResourcesCard />
				</Grid>
			</Grid>
		</EntityLayout.Route>

		<EntityLayout.Route path="/docs" title="Docs">
			{techdocsContent}
		</EntityLayout.Route>

		{/* <EntityLayout.Route path="/soundcheck" title="Soundcheck">
      <EntitySoundcheckContent />
    </EntityLayout.Route> */}

		{githubActionsHealthRoute}
	</EntityLayout>
);

const websiteEntityPage = (
	<EntityLayout>
		<EntityLayout.Route path="/" title="Overview">
			{overviewContent}
		</EntityLayout.Route>

		<EntityLayout.Route path="/code-insights" title="Code Insights">
			<EntityGithubInsightsContent />
		</EntityLayout.Route>

		<EntityLayout.Route path="/ci-cd" title="CI/CD">
			{cicdContent}
		</EntityLayout.Route>

		<EntityLayout.Route
			path="/pull-requests"
			title="Pull Requests"
			if={isGithubPullRequestsAvailable}
		>
			<EntityGithubPullRequestsContent />
		</EntityLayout.Route>

		<EntityLayout.Route path="/dependencies" title="Dependencies">
			<Grid container spacing={3} alignItems="stretch">
				<Grid item md={6}>
					<EntityDependsOnComponentsCard />
				</Grid>
				<Grid item md={6}>
					<EntityDependsOnResourcesCard />
				</Grid>
			</Grid>
		</EntityLayout.Route>

		<EntityLayout.Route path="/docs" title="Docs">
			{techdocsContent}
		</EntityLayout.Route>

		{githubActionsHealthRoute}
	</EntityLayout>
);

const libraryEntityPage = (
	<EntityLayout>
		<EntityLayout.Route path="/" title="Overview">
			{overviewContent}
		</EntityLayout.Route>

		<EntityLayout.Route path="/code-insights" title="Code Insights">
			<EntityGithubInsightsContent />
		</EntityLayout.Route>

		<EntityLayout.Route path="/ci-cd" title="CI/CD">
			{cicdContent}
		</EntityLayout.Route>

		<EntityLayout.Route
			path="/pull-requests"
			title="Pull Requests"
			if={isGithubPullRequestsAvailable}
		>
			<EntityGithubPullRequestsContent />
		</EntityLayout.Route>

		<EntityLayout.Route path="/docs" title="Docs">
			{techdocsContent}
		</EntityLayout.Route>

		{githubActionsHealthRoute}
	</EntityLayout>
);

/**
 * NOTE: This page is designed to work on small screens such as mobile devices.
 * This is based on Material UI Grid. If breakpoints are used, each grid item must set the `xs` prop to a column size or to `true`,
 * since this does not default. If no breakpoints are used, the items will equitably share the available space.
 * https://material-ui.com/components/grid/#basic-grid.
 */

const defaultEntityPage = (
	<EntityLayout>
		<EntityLayout.Route path="/" title="Overview">
			{overviewContent}
		</EntityLayout.Route>

		<EntityLayout.Route path="/code-insights" title="Code Insights">
			<EntityGithubInsightsContent />
		</EntityLayout.Route>

		<EntityLayout.Route
			path="/pull-requests"
			title="Pull Requests"
			if={isGithubPullRequestsAvailable}
		>
			<EntityGithubPullRequestsContent />
		</EntityLayout.Route>

		<EntityLayout.Route path="/docs" title="Docs">
			{techdocsContent}
		</EntityLayout.Route>

		{githubActionsHealthRoute}
	</EntityLayout>
);

const componentPage = (
	<EntitySwitch>
		<EntitySwitch.Case if={isComponentType('service')}>
			{serviceEntityPage}
		</EntitySwitch.Case>

		<EntitySwitch.Case if={isComponentType('website')}>
			{websiteEntityPage}
		</EntitySwitch.Case>

		<EntitySwitch.Case if={isComponentType('library')}>
			{libraryEntityPage}
		</EntitySwitch.Case>

		<EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
	</EntitySwitch>
);

const apiPage = (
	<EntityLayout>
		<EntityLayout.Route path="/" title="Overview">
			<Grid container spacing={3}>
				{entityWarningContent}
				<Grid item md={6}>
					<EntityAboutCard />
				</Grid>
				<Grid item md={6} xs={12}>
					<EntityCatalogGraphCard height={400} />
				</Grid>
				<Grid item md={4} xs={12}>
					<EntityLinksCard />
				</Grid>
				<Grid container item md={12}>
					<Grid item md={6}>
						<EntityProvidingComponentsCard />
					</Grid>
					<Grid item md={6}>
						<EntityConsumingComponentsCard />
					</Grid>
				</Grid>
			</Grid>
		</EntityLayout.Route>

		<EntityLayout.Route path="/definition" title="Definition">
			<Grid container spacing={3}>
				<Grid item xs={12}>
					<EntityApiDefinitionCard />
				</Grid>
			</Grid>
		</EntityLayout.Route>
	</EntityLayout>
);

const userPage = (
	<EntityLayout>
		<EntityLayout.Route path="/" title="Overview">
			<Grid container spacing={3}>
				{entityWarningContent}
				<Grid item xs={12} md={6}>
					<EntityUserProfileCard />
				</Grid>
				<Grid item xs={12} md={6}>
					<EntityOwnershipCard />
				</Grid>
				<Grid item xs={12} md={6}>
					<SkillExchangeUserEntitySkillsCard />
				</Grid>
			</Grid>
		</EntityLayout.Route>
	</EntityLayout>
);

const groupPage = (
	<EntityLayout>
		<EntityLayout.Route path="/" title="Overview">
			<Grid container spacing={3}>
				{entityWarningContent}
				<Grid item xs={12} md={6}>
					<EntityGroupProfileCard />
				</Grid>
				<Grid item xs={12} md={6}>
					<EntityOwnershipCard />
				</Grid>
				<Grid item xs={12} md={6}>
					<EntityTeamMetadataCard />
				</Grid>
				<Grid item xs={12}>
					<EntitySwitch>
						<EntitySwitch.Case if={isGithubTeamPullRequestsAvailable}>
							<EntityGithubGroupPullRequestsCard />
						</EntitySwitch.Case>
					</EntitySwitch>
				</Grid>
				<Grid item xs={12}>
					<EntityMembersListCard />
				</Grid>
			</Grid>
		</EntityLayout.Route>
		{/* <EntityLayout.Route path="/soundcheck-overview" title="Soundcheck Overview">
      <GroupSoundcheckContent />
    </EntityLayout.Route>
    <EntityLayout.Route path="/soundcheck" title="Soundcheck Certifications">
      <EntitySoundcheckContent />
    </EntityLayout.Route> */}
		{githubActionsHealthRoute}
	</EntityLayout>
);

const systemPage = (
	<EntityLayout>
		<EntityLayout.Route path="/" title="Overview">
			<Grid container spacing={3} alignItems="stretch">
				{entityWarningContent}
				<Grid item md={6}>
					<EntityAboutCard />
				</Grid>
				<Grid item md={6} xs={12}>
					<EntityCatalogGraphCard height={400} />
				</Grid>
				<Grid item md={4} xs={12}>
					<EntityLinksCard />
				</Grid>
				<Grid item md={8}>
					<EntityHasComponentsCard />
				</Grid>
				<Grid item md={6}>
					<EntityHasApisCard />
				</Grid>
				<Grid item md={6}>
					<EntityHasResourcesCard />
				</Grid>
			</Grid>
		</EntityLayout.Route>

		<EntityLayout.Route path="/code-insights" title="Code Insights">
			<EntityGithubInsightsContent />
		</EntityLayout.Route>

		<EntityLayout.Route
			path="/pull-requests"
			title="Pull Requests"
			if={isGithubPullRequestsAvailable}
		>
			<EntityGithubPullRequestsContent />
		</EntityLayout.Route>

		<EntityLayout.Route path="/diagram" title="Diagram">
			<EntityCatalogGraphCard
				direction={Direction.LEFT_RIGHT}
				title="System Diagram"
				height={700}
				relations={[
					RELATION_PART_OF,
					RELATION_HAS_PART,
					RELATION_API_CONSUMED_BY,
					RELATION_API_PROVIDED_BY,
					RELATION_CONSUMES_API,
					RELATION_PROVIDES_API,
					RELATION_DEPENDENCY_OF,
					RELATION_DEPENDS_ON,
				]}
				unidirectional={false}
			/>
		</EntityLayout.Route>
	</EntityLayout>
);

const domainPage = (
	<EntityLayout>
		<EntityLayout.Route path="/" title="Overview">
			<Grid container spacing={3} alignItems="stretch">
				{entityWarningContent}
				<Grid item md={6}>
					<EntityAboutCard />
				</Grid>
				<Grid item md={6} xs={12}>
					<EntityCatalogGraphCard height={400} />
				</Grid>
				<Grid item md={6}>
					<EntityHasSystemsCard />
				</Grid>
			</Grid>
		</EntityLayout.Route>
	</EntityLayout>
);

export const entityPage = (
	<EntitySwitch>
		<EntitySwitch.Case if={isKind('component')} children={componentPage} />
		<EntitySwitch.Case if={isKind('api')} children={apiPage} />
		<EntitySwitch.Case if={isKind('group')} children={groupPage} />
		<EntitySwitch.Case if={isKind('user')} children={userPage} />
		<EntitySwitch.Case if={isKind('system')} children={systemPage} />
		<EntitySwitch.Case if={isKind('domain')} children={domainPage} />

		<EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
	</EntitySwitch>
);
